import * as Redux from 'redux';
import { MembersActionTypes, MembersState, Member, MembersEntity, PaymentInfo } from './members.types';
import {
  initialMembersState,
  GET_MEMBERS,
  GET_MEMBER,
  GET_ABSENT_MEMBERS,
  REGISTER_MEMBER,
  UPDATE_MEMBER,
  BULK_CANCEL,
  BULK_ADD_DAYS_TO_END_DATES,
  CREATE_PAYMENT,
  UPDATE_PAYMENT,
  CANCEL_PAYMENT,
  CREATE_NEW_MEMBER,
  initialMemberDetail,
  PAYMENTS_TABLE_ROW_CLICKED,
  PAYMENTS_TABLE_ROW_UNCLICKED,
  NEW_MEMBER_PAYMENT_CLICKED,
  UPDATE_SCHEDULED_PAYMENT,
  ACTIVATE_MEMBERSHIP_BUTTON_CLICKED,
  EXPORT_CSV
} from './members.constants';
import { ReduxAction, SimpleResponse } from '../app/app.types';
import { REVERT_TO_INITIAL_STATE, REVERT_TO_INITIAL_STATE_EXCEPT_AUTH } from '../app/app.const';
import { saveAs } from 'file-saver';

function replaceUndefinedMember(members: Member[]) {
  return !members ? [] : members;
}

function replaceUpdatedMember(members: Member[], updatedMember: Member | null): Member[] {
  if (updatedMember) {
    return [updatedMember, ...members.filter((member) => {
      return member.id !== updatedMember.id;
    })];
  } else {
    return members;
  }
}

function replaceUpdatedPayment(payments: PaymentInfo[], updatedPayment: PaymentInfo | null): PaymentInfo[] {
  if (updatedPayment) {
    return [updatedPayment, ...payments.filter((payment) => {
      return payment.id !== updatedPayment.id;
    })];
  } else {
    return payments;
  }
}

function replaceUpdatedMembers(members: Member[], updatedMembers: Member[]): Member[] {
  if (updatedMembers.length > 0) {
    return [...updatedMembers, ...members
      .filter((m: Member) => !updatedMembers.map((u: Member) => u.id).includes(m.id))
    ];
  } else {
    return members;
  }
}

export const membersReducer: Redux.Reducer<MembersState> =
  (membersState: MembersState = initialMembersState,
    action: ReduxAction<MembersActionTypes, SimpleResponse<MembersEntity>>): MembersState => {
    switch (action.type) {
      case `${UPDATE_SCHEDULED_PAYMENT}_FULFILLED`:
        return {
          ...membersState,
          statusCode: action.payload.statusCode
        };
      case NEW_MEMBER_PAYMENT_CLICKED:
        return {
          ...membersState,
          newMemberPaymentClicked: true,
          paymentsTableRowClicked: false,
          selectedPayment: null
        };
      case PAYMENTS_TABLE_ROW_UNCLICKED:
        return {
          ...membersState,
          newMemberPaymentClicked: false,
          paymentsTableRowClicked: false,
          selectedPayment: null
        };
      case PAYMENTS_TABLE_ROW_CLICKED:
        return {
          ...membersState,
          newMemberPaymentClicked: false,
          paymentsTableRowClicked: true,
          selectedPayment: action.payload.body.entity
        };
      case CREATE_NEW_MEMBER:
        return {
          ...membersState,
          member: initialMemberDetail
        };
      case `${CANCEL_PAYMENT}_FULFILLED`:
        return {
          ...membersState,
          statusCode: action.payload.statusCode,
          member: {
            ...membersState.member,
            payments: [
              action.payload.body.entity,
              ...membersState.member.payments.filter(p => (
                action.payload.body.entity === null || p.id !== action.payload.body.entity.id
              )
              )
            ]
          }
        };
      case `${CREATE_PAYMENT}_FULFILLED`:
        return {
          ...membersState,
          statusCode: action.payload.statusCode,
          member: {
            ...membersState.member,
            payments: [
              action.payload.body.entity,
              ...membersState.member.payments
            ]
          }
        };
      case `${UPDATE_PAYMENT}_FULFILLED`:
        return {
          ...membersState,
          statusCode: action.payload.statusCode,
          member: {
            ...membersState.member,
            payments: replaceUpdatedPayment(membersState.member.payments, action.payload.body.entity)
          }
        };
      case `${GET_MEMBERS}_FULFILLED`:
        return {
          ...membersState,
          statusCode: action.payload.statusCode,
          members: replaceUndefinedMember(action.payload.body.list)
        };
      case `${GET_MEMBER}_FULFILLED`:
        return {
          ...membersState,
          statusCode: action.payload.statusCode,
          member: action.payload.body.entity
        };
      case `${GET_ABSENT_MEMBERS}_FULFILLED`:
        return {
          ...membersState,
          statusCode: action.payload.statusCode,
          members: replaceUndefinedMember(action.payload.body.list)
        };
      case `${REGISTER_MEMBER}_FULFILLED`:
        return {
          ...membersState,
          statusCode: action.payload.statusCode,
          members: action.payload.body.entity ?
            [action.payload.body.entity, ...membersState.members] :
            [...membersState.members],
          newMember: action.payload.body.entity
        };
      case `${UPDATE_MEMBER}_FULFILLED`:
        return {
          ...membersState,
          statusCode: action.payload.statusCode,
          members: action.payload.body.entity ?
            [...replaceUpdatedMember(membersState.members, action.payload.body.entity)] :
            [...membersState.members]
        };
      case `${BULK_CANCEL}_FULFILLED`:
        return {
          ...membersState,
          statusCode: action.payload.statusCode,
          members: [...replaceUpdatedMembers(membersState.members, action.payload.body.list)]
        };
      case `${BULK_ADD_DAYS_TO_END_DATES}_FULFILLED`:
        return {
          ...membersState,
          statusCode: action.payload.statusCode,
          members: [...replaceUpdatedMembers(membersState.members, action.payload.body.list)]
        };
      case `${EXPORT_CSV}_FULFILLED`:
        const blob = new Blob([action.payload.body.msg], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, 'members.csv');
        return membersState;
      case REVERT_TO_INITIAL_STATE:
        return initialMembersState;
      case REVERT_TO_INITIAL_STATE_EXCEPT_AUTH:
        return initialMembersState;
      default:
        return membersState;
    }
  };

export default membersReducer;


// WEBPACK FOOTER //
// ./src/members/members.reducer.tsx